"use client";

import React, { useEffect, useState, useCallback } from "react";
import { getApiUrl } from "@/lib/api";

type ToastItem = {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "warning";
};

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
};

// Global helper functions
export function showToast(message: string, type: ToastItem["type"] = "info") {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("app-show-toast", { detail: { message, type } })
    );
  }
}

export function showConfirm(options: ConfirmOptions) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("app-show-confirm", { detail: options })
    );
  }
}

export function triggerEvictionModal() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("app-show-eviction"));
  }
}

export function ModalToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [evictionOpen, setEvictionOpen] = useState(false);
  const [confirmData, setConfirmData] = useState<ConfirmOptions | null>(null);

  // Add toast helper
  const addToast = useCallback(
    (message: string, type: ToastItem["type"] = "info") => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { id, message, type }]);

      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4000);
    },
    []
  );

  // Listen to global custom events
  useEffect(() => {
    const handleToast = (e: Event) => {
      const customEvent = e as CustomEvent<{
        message: string;
        type: ToastItem["type"];
      }>;
      if (customEvent.detail) {
        addToast(customEvent.detail.message, customEvent.detail.type || "info");
      }
    };

    const handleEviction = () => {
      if (!evictionOpen) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.dispatchEvent(new Event("auth-change"));
        setEvictionOpen(true);
      }
    };

    const handleConfirm = (e: Event) => {
      const customEvent = e as CustomEvent<ConfirmOptions>;
      if (customEvent.detail) {
        setConfirmData(customEvent.detail);
      }
    };

    window.addEventListener("app-show-toast", handleToast);
    window.addEventListener("app-show-eviction", handleEviction);
    window.addEventListener("app-show-confirm", handleConfirm);

    return () => {
      window.removeEventListener("app-show-toast", handleToast);
      window.removeEventListener("app-show-eviction", handleEviction);
      window.removeEventListener("app-show-confirm", handleConfirm);
    };
  }, [addToast, evictionOpen]);

  // Heartbeat Polling: Check session validity every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token || evictionOpen) return;

      const apiUrl = getApiUrl();
      fetch(`${apiUrl}/api/v1/group/auth/me`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (res.status === 401 && localStorage.getItem("token") === token) {
            triggerEvictionModal();
          }
        })
        .catch(() => {});
    }, 4000);

    return () => clearInterval(interval);
  }, [evictionOpen]);

  const handleEvictionConfirm = () => {
    setEvictionOpen(false);
    window.location.href = "/login";
  };

  return (
    <>
      {children}

      {/* 1. Toast Notification Container */}
      <div className="toast-container-root">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast-card toast-${toast.type}`}>
            <span className="toast-icon">
              {toast.type === "success" && "✓"}
              {toast.type === "error" && "✕"}
              {toast.type === "warning" && "⚠️"}
              {toast.type === "info" && "ℹ"}
            </span>
            <span className="toast-message">{toast.message}</span>
          </div>
        ))}
      </div>

      {/* 2. Single Session Eviction Centered Modal Popup */}
      {evictionOpen && (
        <div className="modal-backdrop-root">
          <div className="modal-card-root eviction-card">
            <div className="modal-icon-badge danger-badge">
              <span>⚠️</span>
            </div>
            <h3 className="modal-title">PHIÊN ĐĂNG NHẬP BỊ KẾT THÚC</h3>
            <p className="modal-description">
              Tài khoản của bạn vừa được đăng nhập ở một trình duyệt hoặc thiết
              bị khác. Phiên làm việc hiện tại đã bị kết thúc.
            </p>
            <div className="modal-actions-single">
              <button
                className="btn-modal-primary"
                onClick={handleEvictionConfirm}
              >
                Tôi đã hiểu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Custom Confirm Modal */}
      {confirmData && (
        <div className="modal-backdrop-root">
          <div className="modal-card-root">
            <div className="modal-icon-badge info-badge">
              <span>❓</span>
            </div>
            <h3 className="modal-title">
              {confirmData.title || "XÁC NHẬN THAO TÁC"}
            </h3>
            <p className="modal-description">{confirmData.message}</p>
            <div className="modal-actions">
              <button
                className="btn-modal-secondary"
                onClick={() => {
                  if (confirmData.onCancel) confirmData.onCancel();
                  setConfirmData(null);
                }}
              >
                {confirmData.cancelText || "Hủy bỏ"}
              </button>
              <button
                className="btn-modal-primary"
                onClick={() => {
                  const cb = confirmData.onConfirm;
                  setConfirmData(null);
                  cb();
                }}
              >
                {confirmData.confirmText || "Đồng ý"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scoped FE Design System Styles */}
      <style jsx global>{`
        /* Toast Container */
        .toast-container-root {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 999999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          pointer-events: none;
        }

        .toast-card {
          pointer-events: auto;
          min-width: 290px;
          max-width: 440px;
          padding: 14px 20px;
          border-radius: 12px;
          color: #fff;
          font-family: Arial, "Helvetica Neue", sans-serif;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5), 0 0 15px rgba(49, 236, 255, 0.2);
          animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          border: 1px solid rgba(49, 236, 255, 0.4);
          backdrop-filter: blur(10px);
        }

        .toast-success {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(6, 95, 70, 0.95) 100%);
          border-color: #34d399;
        }

        .toast-error {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(153, 27, 27, 0.95) 100%);
          border-color: #f87171;
        }

        .toast-warning {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(146, 64, 14, 0.95) 100%);
          border-color: #fbbf24;
        }

        .toast-info {
          background: linear-gradient(135deg, rgba(14, 30, 94, 0.95) 0%, rgba(2, 8, 104, 0.95) 100%);
          border-color: #31ecff;
        }

        .toast-icon {
          font-size: 18px;
          font-weight: bold;
        }

        .toast-message {
          line-height: 1.4;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Modal Overlay Backdrop */
        .modal-backdrop-root {
          position: fixed;
          inset: 0;
          z-index: 999990;
          background: rgba(1, 6, 45, 0.88);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeInModal 0.25s ease-out forwards;
        }

        .modal-card-root {
          position: relative;
          background: linear-gradient(180deg, #081a5c 0%, #020933 100%);
          border: 2px solid #31ecff;
          border-radius: 24px;
          padding: 36px 32px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7), 0 0 30px rgba(49, 236, 255, 0.35), inset 0 0 15px rgba(49, 236, 255, 0.15);
          text-align: center;
          animation: scaleUpModal 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          color: #ffffff;
        }

        .modal-icon-badge {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          margin: 0 auto 20px auto;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
        }

        .danger-badge {
          background: radial-gradient(circle, rgba(255, 77, 79, 0.35) 0%, rgba(2, 8, 104, 0.9) 100%);
          border: 2px solid #ff4d4f;
          box-shadow: 0 0 25px rgba(255, 77, 79, 0.6);
        }

        .info-badge {
          background: radial-gradient(circle, rgba(49, 236, 255, 0.35) 0%, rgba(2, 8, 104, 0.9) 100%);
          border: 2px solid #31ecff;
          box-shadow: 0 0 25px rgba(49, 236, 255, 0.6);
        }

        .modal-title {
          font-family: "UTM Nokia", "UTM Cafeta", Arial, sans-serif;
          font-size: 22px;
          font-weight: 700;
          color: #ffffff;
          margin-top: 0;
          margin-bottom: 14px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          text-shadow: 0 0 12px rgba(49, 236, 255, 0.7);
        }

        .modal-description {
          font-family: Arial, "Helvetica Neue", sans-serif;
          font-size: 15px;
          line-height: 1.6;
          color: #d1e9ff;
          margin-top: 0;
          margin-bottom: 28px;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .modal-actions-single {
          display: flex;
          justify-content: center;
        }

        .btn-modal-primary {
          background: linear-gradient(90deg, #00c6ff 0%, #31ecff 100%);
          color: #020868;
          font-family: "UTM Nokia", "UTM Cafeta", Arial, sans-serif;
          font-size: 17px;
          font-weight: 700;
          padding: 14px 28px;
          border-radius: 14px;
          border: 1px solid #7bf3ff;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 0 20px rgba(49, 236, 255, 0.5);
          width: 100%;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .btn-modal-primary:hover {
          transform: translateY(-2px);
          filter: brightness(1.15);
          box-shadow: 0 0 28px rgba(49, 236, 255, 0.85);
        }

        .btn-modal-secondary {
          background: rgba(255, 255, 255, 0.1);
          color: #d1e9ff;
          font-family: "UTM Cafeta", Arial, sans-serif;
          font-size: 16px;
          font-weight: 600;
          padding: 14px 24px;
          border-radius: 14px;
          border: 1px solid rgba(49, 236, 255, 0.3);
          cursor: pointer;
          transition: all 0.2s ease;
          width: 50%;
        }

        .btn-modal-secondary:hover {
          background: rgba(49, 236, 255, 0.2);
          color: #fff;
          border-color: #31ecff;
        }

        @keyframes fadeInModal {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleUpModal {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
